import { useEffect, useState } from 'react';

// project imports
import { ApplicableAction, EAIEntityTypeCode } from 'utils/constants';
import { useAppSelector } from 'hooks/useAppStore';
import { selectEaiContextActions } from 'features/eai/selectors';
import { selectDispatcherContextActions } from 'features/dispatcher/selectors/dispatcherSelectors';

// types
import { UseContextProps } from 'models';
import {
  CaCodeType,
  ContextAction,
  ResourceContextAction
} from 'models/contextActions';
import { selectAccountInfo } from 'features/account/accountSelectors';

type useContextActionReturningType = {
  contextAction: ContextAction | undefined;
  isAllowed: boolean;
  isContextActionReady: boolean;
};

const getActionByCode = (
  resourceContextAction: ResourceContextAction,
  actionCode: CaCodeType
): ContextAction | undefined => {
  return resourceContextAction.contextActions.find(
    (contextAction) => contextAction.code === actionCode
  );
};

const mergeContextActions = (
  arr: ResourceContextAction[]
): ResourceContextAction[] => {
  const mergedObjects: any = {};

  arr.forEach(({ resourceEntity, identificator, contextActions }) => {
    if (resourceEntity) {
      const id = `${resourceEntity}-${identificator}`;
      if (!mergedObjects[id]) {
        mergedObjects[id] = {
          resourceEntity,
          identificator,
          contextActions
        };
      }

      mergedObjects[id].contextActions = [
        ...mergedObjects[id].contextActions,
        ...contextActions
      ];
    }
  });

  return Object.values(mergedObjects);
};

// ==============================|| HOOKS ||============================== //

export const useContextAction = ({
  actionCode,
  resourceEntity,
  entityIdentificator
}: UseContextProps): useContextActionReturningType => {
  const [contextAction, setContextAction] = useState<
    ContextAction | undefined
  >();
  const eaiContextActions: ResourceContextAction[] = useAppSelector(
    selectEaiContextActions
  );
  const dispatcherContextActions: ResourceContextAction[] = useAppSelector(
    selectDispatcherContextActions
  );

  const accountInfo = useAppSelector(selectAccountInfo);
  useEffect(() => {
    const resourceContextActions: ResourceContextAction[] = mergeContextActions(
      [...eaiContextActions, ...dispatcherContextActions]
    );

    const setAction = (
      resourceContextAction: ResourceContextAction | undefined
    ): boolean => {
      if (resourceContextAction) {
        const actionByCode = getActionByCode(resourceContextAction, actionCode);

        if (actionByCode) {
          setContextAction(actionByCode);
          return true;
        }
      }

      return false;
    };

    if (resourceEntity && entityIdentificator) {
      setAction(
        resourceContextActions.find(
          (item) =>
            item.resourceEntity === resourceEntity &&
            item.identificator?.toString() === entityIdentificator?.toString()
        )
      );
    } else {
      const ca: ResourceContextAction | undefined = resourceContextActions.find(
        (item) =>
          item.resourceEntity === EAIEntityTypeCode.USER &&
          item.identificator?.toString() === accountInfo?.sub
      );
      ca && setAction(ca);
    }
  }, [
    eaiContextActions,
    dispatcherContextActions,
    actionCode,
    resourceEntity,
    entityIdentificator,
    accountInfo?.sub
  ]);

  return {
    contextAction,
    isAllowed: contextAction?.applicability === ApplicableAction.ENABLED,
    isContextActionReady: contextAction?.applicability != null
  };
};
